import ConfirmAbortDialog from '@common/components/General/ConfirmAbortDialog';
import TextInput from '@common/components/Inputs/TextInput';
import { DeleteIcon } from '@common/utils/icons/icons';
import { SelectChangeEvent } from '@mui/material';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import SelectInput from 'components/forms/SelectInput';
import { FleetDataModel } from 'models/enums/fleet-management';
import FleetDataSet from 'models/fleet-management/FleetDataSet';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'store';
import { deleteDataSetById } from 'store/fleet-management/fleet-management-actions';

const GeneralDatasetConfigs: React.FC<{
  dataset: FleetDataSet;
  isEditing: boolean;
  onEdit: (ds: Partial<FleetDataSet>) => void;
}> = ({ dataset, isEditing, onEdit }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  const nameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    onEdit({ name: event.target.value });
  };

  const descriptionChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    onEdit({ description: event.target.value });
  };

  const modelChangeHandler = (event: SelectChangeEvent<unknown>) => {
    onEdit({
      model: event.target.value as FleetDataModel,
      fields: [],
      filters: [],
    });
  };

  const deleteClickHandler = () => {
    setIsConfirmDeleteOpen(true);
  };

  const abortDeleteHandler = () => {
    setIsConfirmDeleteOpen(false);
  };

  const confirmDeleteHandler = () => {
    setIsConfirmDeleteOpen(false);
    dispatch(deleteDataSetById(dataset.id));
  };

  return (
    <React.Fragment>
      <Stack spacing={4}>
        <Stack
          spacing={1}
          sx={(theme) => ({
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            p: 2,
          })}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            height={'36.5px'}
          >
            <Typography variant="overline">{t('title-general-dataset-properties')}</Typography>
            {isEditing && (
              <Stack direction="row" justifyContent="flex-end">
                <Button onClick={deleteClickHandler} startIcon={<DeleteIcon />} color="error">
                  {t('button-delete')}
                </Button>
              </Stack>
            )}
          </Stack>
          <TextInput
            value={dataset.name}
            onChange={nameChangeHandler}
            disabled={!isEditing}
            size="small"
            sx={(theme) => ({
              '& .MuiOutlinedInput-notchedOutline': { borderBottom: isEditing ? undefined : 0 },
              '& .Mui-disabled': {
                WebkitTextFillColor: isEditing ? undefined : theme.palette.text.primary,
              },
            })}
          />
          <TextInput
            size="small"
            value={dataset.description ?? ''}
            placeholder={t('input-label-description')}
            onChange={descriptionChangeHandler}
            sx={(theme) => ({
              '& .MuiOutlinedInput-notchedOutline': { borderBottom: isEditing ? undefined : 0 },
              '& .Mui-disabled': {
                WebkitTextFillColor: isEditing ? undefined : theme.palette.text.primary,
              },
            })}
            disabled={!isEditing}
          />
          <SelectInput
            size="small"
            value={dataset.model}
            onChange={modelChangeHandler}
            disabled={!isEditing}
            sx={(theme) => ({
              '& .MuiOutlinedInput-notchedOutline': { borderBottom: isEditing ? undefined : 0 },
              '& .Mui-disabled': {
                WebkitTextFillColor: isEditing ? undefined : theme.palette.text.primary,
              },
            })}
          >
            {Object.values(FleetDataModel).map((model) => (
              <MenuItem key={model} value={model}>
                {model}
              </MenuItem>
            ))}
          </SelectInput>
        </Stack>
      </Stack>
      <ConfirmAbortDialog
        isOpen={isConfirmDeleteOpen}
        title={t('title-delete-dashboard')}
        description={t('info-delete-dashboard')}
        abortButtonText={t('button-cancel')}
        confirmButtonText={t('button-confirm')}
        onConfirm={confirmDeleteHandler}
        onAbort={abortDeleteHandler}
      />
    </React.Fragment>
  );
};

export default GeneralDatasetConfigs;
